import {
  BalanceSheetCellPosition,
  BalanceSheetDensity,
} from './BalanceSheetTable.types';

export const BALANCE_SHEET_COLUMN_GAP_WIDTH = 4;

/** Entity reports reserve 50% for names, 32% for details, and 18% for values. */
export const ENTITY_GRID_TEMPLATE_COLUMNS =
  'minmax(0, 50fr) minmax(0, 32fr) minmax(0, 18fr)';

/**
 * Height of the header group row (e.g. "IN ESTATE", "OUT OF ESTATE") at default density.
 * Used as the `top` offset for the sticky column header row beneath it.
 * Calculation: 12px paddingTop + 16px lineHeight + 12px paddingBottom + 2px borderBottom = 42px
 */
export const HEADER_GROUP_ROW_HEIGHT = 42;

/**
 * Padding (in px) applied to the Card that wraps the balance sheet grid.
 * Used both as the Card's padding and to offset the grid's max-height calculation.
 */
export const BALANCE_SHEET_CARD_PADDING = 16;

interface BalanceSheetPaddingOptions {
  indentLeft?: number;
}

/**
 * Shared vertical padding for balance sheet cells/headers across densities.
 */
function getBalanceSheetVerticalPadding(density: BalanceSheetDensity): number {
  switch (density) {
    case 'default':
      return 12;
    case 'dense':
      return 8;
    case 'extra-dense':
      return 4;
  }
}

/**
 * Shared horizontal padding for balance sheet cells/headers across positions.
 * Optional indentation is only applied to start-position rows.
 */
function getBalanceSheetHorizontalPadding(
  cellPosition: BalanceSheetCellPosition,
  { indentLeft = 0 }: BalanceSheetPaddingOptions = {}
): { paddingLeft: number; paddingRight: number } {
  switch (cellPosition) {
    case 'start':
      return { paddingLeft: 12 + indentLeft, paddingRight: 6 };
    case 'middle':
      return { paddingLeft: 6, paddingRight: 6 };
    case 'end':
      return { paddingLeft: 6, paddingRight: 12 };
    case 'summary':
      return { paddingLeft: 12, paddingRight: 12 };
  }
}

/**
 * Shared full padding object for balance sheet cells/headers.
 */
export function getBalanceSheetPadding(
  density: BalanceSheetDensity,
  cellPosition: BalanceSheetCellPosition,
  options?: BalanceSheetPaddingOptions
): {
  paddingTop: number;
  paddingBottom: number;
  paddingLeft: number;
  paddingRight: number;
} {
  const verticalPadding = getBalanceSheetVerticalPadding(density);
  const horizontalPadding = getBalanceSheetHorizontalPadding(
    cellPosition,
    options
  );

  return {
    paddingTop: verticalPadding,
    paddingBottom: verticalPadding,
    ...horizontalPadding,
  };
}
